<template>
  <div
    class="royal-join-page"
    :class="{ 'profile-crop-active': cropOpen }"
    :lang="$i18n.locale === 'jp' ? 'ja' : $i18n.locale"
  >
    <div class="petal-layer" aria-hidden="true">
      <img
        v-for="petal in fallingPetals"
        :key="petal.id"
        class="royal-petal"
        src="@/assets/auth/join/rose-petal.png"
        alt=""
        :style="{
          '--petal-left': petal.left,
          '--petal-delay': petal.delay,
          '--petal-duration': petal.duration,
          '--petal-size': petal.size,
          '--petal-sway': petal.sway,
          '--petal-turn': petal.turn,
        }"
      />
    </div>

    <main class="royal-invitation">
      <header class="royal-header">
        <v-img class="royal-logo mx-auto" contain src="@/assets/logo/main.svg"></v-img>
      </header>

      <section class="mirror-heading">
        <h1>{{ $t("page.register.mirror-title") }}</h1>
        <div class="royal-divider" aria-hidden="true">
          <span></span>
          <v-icon class="mirror-crown" color="#c9974d">mdi-crown</v-icon>
          <span></span>
        </div>
      </section>

      <section class="royal-fields" :aria-label="$t('page.register.title')">
        <v-text-field
          class="royal-field"
          outlined
          dense
          prepend-inner-icon="mdi-account-outline"
          append-icon="mdi-star-four-points"
          v-model="temp_sns_user.user_id"
          :label="$t('page.login.id')"
          readonly
          hide-details="auto"
        ></v-text-field>

        <v-text-field
          class="royal-field"
          outlined
          dense
          prepend-inner-icon="mdi-crown"
          append-icon="mdi-star-four-points"
          v-model.trim="name"
          :placeholder="$t('page.mypage.info.name')"
          hide-details="auto"
          @change="$v.name.$touch()"
          @blur="$v.name.$touch()"
          :error-messages="name_Errors"
        ></v-text-field>

        <v-text-field
          class="royal-field"
          outlined
          dense
          prepend-inner-icon="mdi-phone-outline"
          append-icon="mdi-star-four-points"
          v-model.trim="phone"
          :placeholder="$t('page.mypage.info.phone')"
          type="tel"
          inputmode="numeric"
          hide-details="auto"
          @change="$v.phone.$touch()"
          @blur="$v.phone.$touch()"
          :error-messages="phone_Errors"
        ></v-text-field>
      </section>

      <section class="mirror-section">
        <p id="mirror-photo-guidance" class="mirror-guidance">{{ $t("page.register.mirror-description") }}</p>

        <input ref="profileInput" class="profile-file-input" type="file" accept="image/*" @change="onProfileSelected" />

        <button
          type="button"
          class="mirror-picker"
          :aria-label="$t('page.register.mirror-upload')"
          aria-describedby="mirror-photo-guidance"
          @click="openProfilePicker"
        >
          <span class="mirror-glass">
            <img v-if="profilePreviewSource" class="mirror-profile-image" :src="profilePreviewSource" alt="" />
            <span v-else class="mirror-empty-state">
              <v-icon size="52" color="#b98563">mdi-camera-plus</v-icon>
            </span>
          </span>
          <img class="mirror-frame" src="@/assets/auth/join/antique-mirror-frame.png" alt="" aria-hidden="true" />
          <span class="mirror-ribbon">
            <img src="@/assets/auth/join/mirror-ribbon.png" alt="" aria-hidden="true" />
            <strong>{{ profilePreviewSource ? $t("page.register.mirror-change") : $t("page.register.mirror-upload") }}</strong>
          </span>
        </button>
      </section>

      <button type="button" class="royal-submit" :disabled="submitting" @click="submit">
        <span class="royal-submit-label">
          <v-progress-circular v-if="submitting" indeterminate size="19" width="2" color="white"></v-progress-circular>
          <v-icon v-else class="royal-submit-crown" size="20">mdi-crown</v-icon>
          <strong>{{ $t("page.register.royal-submit") }}</strong>
        </span>
      </button>
    </main>

    <transition name="profile-crop-fade">
      <div v-if="cropOpen" class="profile-crop-overlay">
        <section
          ref="cropDialog"
          class="profile-crop-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-crop-title"
          tabindex="-1"
        >
          <header class="profile-crop-header">
            <span class="profile-crop-crown" aria-hidden="true">
              <v-icon size="21">mdi-crown</v-icon>
            </span>
            <div>
              <h2 id="profile-crop-title">{{ $t("page.register.profile-editor-title") }}</h2>
              <p>{{ $t("page.register.profile-editor-description") }}</p>
            </div>
          </header>

          <div class="profile-crop-stage">
            <clipper-fixed
              v-if="cropSource"
              ref="profileCropper"
              class="profile-cropper"
              :src="cropSource"
              :ratio="mirrorCropRatio"
              :area="76"
              :round="true"
              :grid="true"
              :min-scale="cropMinScale"
              :zoom-rate="0.06"
              :handle-zoom-event="handleCropZoom"
              bg-color="#fff8f3"
              border-color="#f2c77e"
              shadow="rgba(30, 12, 21, 0.72)"
              @load="onCropImageLoaded"
              @error="onCropImageError"
            ></clipper-fixed>
            <span class="profile-crop-gesture" aria-hidden="true">
              <v-icon size="18">mdi-gesture-swipe</v-icon>
            </span>
          </div>

          <div class="profile-crop-controls">
            <button
              type="button"
              class="profile-crop-zoom-button"
              :aria-label="$t('page.register.profile-editor-zoom-out')"
              :disabled="cropSaving"
              @click="adjustCropScale(-0.12)"
            >
              <v-icon size="21">mdi-minus</v-icon>
            </button>
            <input
              v-model.number="cropScale"
              class="profile-crop-range"
              type="range"
              :min="cropMinScale"
              :max="cropMaxScale"
              step="0.01"
              :aria-label="$t('page.register.profile-editor-zoom')"
              :disabled="cropSaving"
              @input="applyCropScale"
            />
            <button
              type="button"
              class="profile-crop-zoom-button"
              :aria-label="$t('page.register.profile-editor-zoom-in')"
              :disabled="cropSaving"
              @click="adjustCropScale(0.12)"
            >
              <v-icon size="21">mdi-plus</v-icon>
            </button>
          </div>

          <button type="button" class="profile-crop-reset" :disabled="cropSaving" @click="resetCropPosition">
            <v-icon size="17">mdi-restore</v-icon>
            <span>{{ $t("page.register.profile-editor-reset") }}</span>
          </button>

          <div class="profile-crop-actions">
            <button type="button" class="profile-crop-cancel" :disabled="cropSaving" @click="cancelProfileCrop">
              {{ $t("page.register.profile-editor-cancel") }}
            </button>
            <button type="button" class="profile-crop-apply" :disabled="cropSaving" @click="applyProfileCrop">
              <v-progress-circular v-if="cropSaving" indeterminate size="18" width="2" color="#fff4cf"></v-progress-circular>
              <span>{{ cropSaving ? $t("page.register.profile-editor-saving") : $t("page.register.profile-editor-apply") }}</span>
            </button>
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
// 검증
import { validationMixin } from "vuelidate"
import { required } from "vuelidate/lib/validators"
import authService from "@/api/auth-service"
import { hasJoinAgreed } from "@/util/joinAgreement"
const { createTravelerLoginDestination } = require("@/util/travelerAttribution")
import { mapMutations, mapState } from "vuex"

export default {
  mixins: [validationMixin],

  validations: {
    name: {
      required,
    },

    phone: {
      required,
    },
  },

  data: () => ({
    // 입력값
    user_id: "",
    name: "",
    phone: "",
    profileFile: null,
    profilePreview: "",
    cropOpen: false,
    cropSource: "",
    cropOriginalFile: null,
    cropScale: 1,
    cropMinScale: 0.1,
    cropMaxScale: 3,
    cropSaving: false,
    mirrorCropRatio: 1038 / 1516,
    submitting: false,
    fallingPetals: [
      { id: 1, left: "5%", delay: "-2s", duration: "13s", size: "27px", sway: "18vw", turn: "540deg" },
      { id: 2, left: "17%", delay: "-9s", duration: "16s", size: "19px", sway: "-12vw", turn: "-420deg" },
      { id: 3, left: "35%", delay: "-5s", duration: "12s", size: "23px", sway: "15vw", turn: "480deg" },
      { id: 4, left: "54%", delay: "-12s", duration: "17s", size: "31px", sway: "-18vw", turn: "-600deg" },
      { id: 5, left: "69%", delay: "-4s", duration: "14s", size: "21px", sway: "11vw", turn: "450deg" },
      { id: 6, left: "83%", delay: "-10s", duration: "15s", size: "26px", sway: "-14vw", turn: "-520deg" },
      { id: 7, left: "94%", delay: "-7s", duration: "18s", size: "18px", sway: "-20vw", turn: "650deg" },
    ],
  }),

  computed: {
    ...mapState(["temp_sns_user"]),
    profilePreviewSource() {
      return this.profilePreview
    },
    // 실명 Error 문구
    name_Errors() {
      const errors = []
      if (!this.$v.name.$dirty) return errors
      !this.$v.name.required && errors.push(this.$t("page.findId.warning-input-name"))
      return errors
    },

    // 연락처 Error 문구
    phone_Errors() {
      const errors = []
      if (!this.$v.phone.$dirty) return errors
      !this.$v.phone.required && errors.push(this.$t("page.findId.warning-input-phone"))
      return errors
    },
  },

  created() {
    if (!hasJoinAgreed()) {
      this.$router.replace({ path: "/auth/join/agree", query: this.$route.query })
    }
  },

  methods: {
    ...mapMutations(["login", "DELETE_TEMP_SNS_USER"]),

    openProfilePicker() {
      this.$refs.profileInput.click()
    },

    onProfileSelected(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      if (!file.type.startsWith("image/")) {
        alert(this.$t("page.register.profile-image-type-error"))
        event.target.value = ""
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        alert(this.$t("page.register.profile-image-size-error"))
        event.target.value = ""
        return
      }

      this.clearCropSource()
      this.cropOriginalFile = file
      this.cropSource = URL.createObjectURL(file)
      this.cropMinScale = 0.1
      this.cropMaxScale = 3
      this.cropScale = 1
      this.cropOpen = true
      event.target.value = ""

      this.$nextTick(() => {
        if (this.$refs.cropDialog) this.$refs.cropDialog.focus()
      })
    },

    onCropImageLoaded() {
      this.$nextTick(() => {
        const cropper = this.$refs.profileCropper
        if (!cropper) return

        const initialScale = Number(cropper.bgWH$) || 1
        const cropArea = cropper.areaEl && cropper.areaEl.getBoundingClientRect()
        const cropImage = cropper.translateEl && cropper.translateEl.getBoundingClientRect()
        const coverScale =
          cropArea && cropImage
            ? initialScale * Math.max(cropArea.width / cropImage.width, cropArea.height / cropImage.height, 1)
            : initialScale

        this.cropMinScale = Math.max(coverScale, 0.1)
        this.cropMaxScale = Math.max(this.cropMinScale * 3, this.cropMinScale + 1)
        this.cropScale = this.cropMinScale
        cropper.setWH$.next(this.cropMinScale)
      })
    },

    onCropImageError() {
      alert(this.$t("page.register.profile-editor-error"))
      this.cancelProfileCrop()
    },

    handleCropZoom(scale) {
      const limitedScale = Math.min(this.cropMaxScale, Math.max(this.cropMinScale, scale))
      this.cropScale = limitedScale
      return limitedScale
    },

    applyCropScale() {
      const cropper = this.$refs.profileCropper
      if (!cropper) return

      const limitedScale = Math.min(this.cropMaxScale, Math.max(this.cropMinScale, Number(this.cropScale)))
      this.cropScale = limitedScale
      cropper.setWH$.next(limitedScale)
    },

    adjustCropScale(amount) {
      this.cropScale = Math.min(this.cropMaxScale, Math.max(this.cropMinScale, Number(this.cropScale) + amount))
      this.applyCropScale()
    },

    resetCropPosition() {
      const cropper = this.$refs.profileCropper
      if (!cropper) return

      this.cropScale = this.cropMinScale
      cropper.setTL$.next({ left: 0, top: 0 })
      cropper.setWH$.next(this.cropMinScale)
    },

    createCroppedProfileFile(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("profile crop failed"))
            return
          }

          resolve(
            new File([blob], `lafia-profile-${Date.now()}.png`, {
              type: "image/png",
              lastModified: Date.now(),
            })
          )
        }, "image/png")
      })
    },

    async applyProfileCrop() {
      const cropper = this.$refs.profileCropper
      if (!cropper || this.cropSaving) return

      this.cropSaving = true
      try {
        const clippedCanvas = cropper.clip()
        const outputCanvas = document.createElement("canvas")
        const outputWidth = 800
        const outputHeight = Math.round(outputWidth / this.mirrorCropRatio)
        outputCanvas.width = outputWidth
        outputCanvas.height = outputHeight
        outputCanvas.getContext("2d").drawImage(clippedCanvas, 0, 0, outputWidth, outputHeight)
        const croppedFile = await this.createCroppedProfileFile(outputCanvas)

        if (this.profilePreview) URL.revokeObjectURL(this.profilePreview)
        this.profileFile = croppedFile
        this.profilePreview = URL.createObjectURL(croppedFile)
        this.cropOpen = false
        this.clearCropSource()
      } catch (error) {
        console.error(error)
        alert(this.$t("page.register.profile-editor-error"))
      } finally {
        this.cropSaving = false
      }
    },

    cancelProfileCrop() {
      if (this.cropSaving) return
      this.cropOpen = false
      this.clearCropSource()
    },

    clearCropSource() {
      if (this.cropSource) URL.revokeObjectURL(this.cropSource)
      this.cropSource = ""
      this.cropOriginalFile = null
    },

    async uploadProfileImage(userId) {
      const formData = new FormData()
      formData.append("image", this.profileFile)

      const uploadResponse = await this.$http.post("/api/image/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          type: "profile_image",
        },
      })

      const profileImageFileName = uploadResponse.data
      const updateResponse = await this.$http.post("/api/user/update/profile_image", {
        params: {
          change_user_img: profileImageFileName,
          user_id: userId,
        },
      })

      if (!updateResponse.data) throw new Error("profile image update failed")
      return profileImageFileName
    },

    // 가입하기
    submit: _.debounce(async function () {
      if (this.submitting) return
      this.user_id_dup_error = false
      // 입력값 기본 검증
      this.$v.$touch()

      // 입력값 기본 검증 통과 시
      if (!this.$v.$invalid) {
        this.submitting = true
        let snsUser = this.temp_sns_user
        try {
          snsUser = {
            ...snsUser,
            name: this.name,
            phone: this.phone,
            password: snsUser.sns_id,
            email: snsUser.user_id,
            type: "SNS",
          }
          delete snsUser.profile_image
          delete snsUser.sns_profile_image

          // db 에 저장
          await authService.doUserInsert(snsUser)

          if (this.profileFile) {
            try {
              snsUser.profile_image = await this.uploadProfileImage(snsUser.user_id)
            } catch (profileError) {
              console.error(profileError)
              alert(this.$t("page.register.profile-image-upload-error"))
            }
          }

          this.DELETE_TEMP_SNS_USER()

          try {
            if (this.$route.query.qrcode) {
              await authService.updateQrCode(snsUser.user_id, this.$route.query.qrcode)
            } else if (this.$route.query.visitQrcode) {
              await authService.updatePayQrcode(snsUser.user_id, this.$route.query.visitQrcode)
            } else if (this.$route.query.pay_qrcode) {
              await authService.updatePayQrcode(snsUser.user_id, this.$route.query.pay_qrcode)
            }
          } catch (e) {
            console.log(e)
            alert(this.$t("alert.error_failed_qrocde_authentication"))
            this.$router.replace("/auth/login")
            return
          }

          // 가입하면 로그인 열쇠까지 받는다. 열쇠 없이 '로그인됨'만 남으면 결제 직전 예약 칸 저장이 401로 막힌다(10/16~17 새 가입 손님 5명 모두).
          try {
            await this.$store.dispatch("login", { userId: snsUser.user_id, password: snsUser.password })
          } catch (e) {
            console.error("가입 뒤 로그인 열쇠 받기 실패", e)
          }
          this.login(snsUser)
          this.$router.replace(createTravelerLoginDestination(this.$route.query))
        } catch (e) {
          console.log(e)
          alert(this.$t("alert.error_page"))
        } finally {
          this.submitting = false
        }
      }
    }, 500),
  },

  beforeDestroy() {
    if (this.profilePreview) URL.revokeObjectURL(this.profilePreview)
    this.clearCropSource()
  },
}
</script>
<style scoped>
.royal-join-page {
  box-sizing: border-box;
  position: relative;
  isolation: isolate;
  width: min(100%, 720px);
  min-height: 1254px;
  margin: 0 auto;
  overflow: visible;
  background: #fffaf6 url("~@/assets/auth/join/royal-invitation-background-v2.png") top center / 100% 100% no-repeat;
}

.royal-join-page.profile-crop-active {
  z-index: 1000;
}

.royal-invitation {
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  width: min(100%, 640px);
  margin: 0 auto;
  padding: 40px 72px 54px;
  text-align: center;
}

.royal-header {
  margin-bottom: 24px;
}

.royal-logo {
  width: 176px;
  height: 54px;
}

.royal-fields {
  width: min(100%, 480px);
  margin: 37px auto 0;
}

.royal-field {
  margin: 0;
}

.royal-field + .royal-field {
  margin-top: 10px;
}

.royal-field.v-text-field--outlined >>> fieldset {
  border-color: rgba(218, 148, 149, 0.5);
  border-width: 1px;
  border-radius: 13px;
}

.royal-field.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #d47d94;
  border-width: 2px;
}

.royal-field.v-text-field--outlined >>> label {
  color: #7f716c;
  font-size: 14px;
  font-weight: 400;
}

.royal-field >>> .v-input__prepend-inner {
  margin-right: 9px;
}

.royal-field >>> .v-input__prepend-inner .v-icon,
.royal-field >>> .v-input__append-inner .v-icon {
  color: #c29a6c;
}

.royal-field >>> .v-input__control {
  min-height: 56px !important;
  background: transparent;
  box-shadow: none;
}

.royal-field >>> .v-input__slot {
  min-height: 56px !important;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.58);
  background-clip: padding-box;
  border-radius: 13px;
  box-shadow: 0 4px 18px rgba(163, 106, 84, 0.035);
}

.royal-field >>> input {
  color: #54423e;
  font-size: 15px;
}

.mirror-section {
  margin-top: 18px;
}

.mirror-heading {
  max-width: 520px;
  margin: 0 auto;
}

.mirror-heading h1 {
  margin: 0;
  color: #a95f50;
  font-family: "Cafe24Danjunghae", "Noto Serif KR", serif;
  font-size: clamp(27px, 4.8vw, 37px);
  font-weight: 400;
  letter-spacing: -0.04em;
  line-height: 1.3;
}

.royal-divider {
  display: flex;
  width: min(100%, 360px);
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 4px auto 2px;
  line-height: 18px;
}

.royal-divider span {
  width: 42%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(201, 151, 77, 0.7));
}

.royal-divider span:last-child {
  background: linear-gradient(90deg, rgba(201, 151, 77, 0.7), transparent);
}

.mirror-crown {
  flex: 0 0 auto;
  font-size: 18px;
}

.mirror-guidance {
  max-width: 420px;
  margin: 0 auto;
  color: #625450;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
  word-break: keep-all;
}

.royal-join-page:lang(ko) .mirror-guidance {
  font-family: "Cafe24Danjunghae", "Noto Serif KR", serif;
  font-weight: 400;
  letter-spacing: -0.02em;
}

.profile-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.mirror-picker {
  position: relative;
  display: block;
  width: min(72vw, 390px);
  aspect-ratio: 1038 / 1516;
  margin: 4px auto -6px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 48%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mirror-picker:focus-visible {
  outline: 3px solid rgba(212, 125, 148, 0.72);
  outline-offset: 4px;
}

.mirror-glass {
  position: absolute;
  z-index: 1;
  top: 24%;
  right: 18%;
  bottom: 13%;
  left: 19%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(circle at 50% 42%, rgba(255, 255, 255, 0.97), rgba(255, 240, 225, 0.86));
  border: 1px solid rgba(222, 179, 130, 0.55);
  border-radius: 50%;
  box-shadow: inset 0 0 32px rgba(255, 255, 255, 0.9), 0 0 26px rgba(225, 159, 130, 0.22);
}

.mirror-frame {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.mirror-profile-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.mirror-empty-state {
  display: flex;
  width: 58%;
  aspect-ratio: 1 / 1.28;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 12px;
  color: #9d6745;
  border: 2px dashed rgba(221, 153, 111, 0.56);
  border-radius: 50%;
}

.mirror-ribbon {
  position: absolute;
  z-index: 3;
  right: -8%;
  bottom: 6%;
  left: -8%;
  display: grid;
  place-items: center;
  pointer-events: none;
}

.mirror-ribbon img {
  grid-area: 1 / 1;
  width: 100%;
  height: auto;
}

.mirror-ribbon strong {
  position: absolute;
  top: 68%;
  left: 50%;
  width: 68%;
  margin: 0;
  color: #74483d;
  font-family: "Cafe24Danjunghae", "Noto Serif KR", serif;
  font-size: clamp(15px, 4.3vw, 22px);
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.15;
  text-align: center;
  text-shadow: 0 1px rgba(255, 255, 255, 0.62);
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.royal-submit {
  position: relative;
  isolation: isolate;
  display: flex;
  width: min(100%, 340px);
  min-height: 58px;
  align-items: center;
  justify-content: center;
  margin: 48px auto 0;
  padding: 0 24px;
  overflow: hidden;
  color: #fff4cf;
  background:
    radial-gradient(circle at 50% -30%, rgba(255, 221, 185, 0.52), transparent 45%),
    linear-gradient(112deg, #7e2948 0%, #b94f72 34%, #d27491 50%, #a63d64 68%, #742440 100%);
  background-position: 0% 50%;
  background-size: 180% 100%;
  border: 1px solid rgba(255, 226, 168, 0.96);
  border-radius: 17px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.54),
    inset 0 -2px 0 rgba(86, 22, 48, 0.28),
    0 0 0 3px rgba(189, 124, 104, 0.2),
    0 8px 20px rgba(116, 42, 67, 0.25),
    0 0 24px rgba(222, 95, 151, 0.2);
  cursor: pointer;
}

.royal-submit::before,
.royal-submit::after {
  position: absolute;
  content: "";
  pointer-events: none;
}

.royal-submit::before {
  z-index: 1;
  inset: 3px;
  border: 1px solid rgba(255, 242, 207, 0.36);
  border-radius: 13px;
}

.royal-submit::after {
  z-index: 0;
  inset: -1px;
  width: 48%;
  background: linear-gradient(105deg, transparent 8%, rgba(255, 247, 218, 0.08) 32%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 221, 236, 0.12) 68%, transparent 92%);
  transform: translate3d(-125%, 0, 0) skewX(-14deg);
}

.royal-submit-label {
  z-index: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 9px;
  font-family: "Cafe24Danjunghae", "Noto Serif KR", serif;
  font-size: clamp(17px, 4.6vw, 20px);
  font-weight: 400;
  letter-spacing: -0.02em;
}

.royal-submit-label strong {
  color: #fff3c7;
  font-weight: 700;
  text-shadow:
    0 1px 2px rgba(79, 20, 42, 0.95),
    0 0 7px rgba(255, 225, 165, 0.36);
}

.royal-join-page:lang(ja) .mirror-heading h1,
.royal-join-page:lang(ja) .mirror-ribbon strong,
.royal-join-page:lang(ja) .royal-submit-label {
  font-family: "GN-Kin-iro_SansSerif", "Yu Mincho", "Hiragino Mincho ProN", serif;
  letter-spacing: 0;
}

.royal-join-page:lang(ja) .mirror-ribbon strong {
  width: 72%;
  font-size: clamp(13px, 3.8vw, 19px);
}

.royal-submit-crown {
  color: #ffda8c !important;
  filter:
    drop-shadow(0 1px 1px rgba(72, 21, 40, 0.9))
    drop-shadow(0 0 5px rgba(255, 220, 139, 0.58));
}

.royal-submit:focus-visible {
  outline: 3px solid rgba(212, 125, 148, 0.72);
  outline-offset: 3px;
}

.royal-submit:disabled {
  cursor: wait;
  opacity: 0.72;
}

.profile-crop-overlay {
  box-sizing: border-box;
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(34, 17, 26, 0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.profile-crop-dialog {
  box-sizing: border-box;
  width: min(100%, 480px);
  max-height: calc(100dvh - 36px);
  overflow-y: auto;
  padding: 22px;
  color: #56423f;
  background:
    radial-gradient(circle at 50% 0%, rgba(255, 227, 214, 0.72), transparent 35%),
    #fffaf5;
  border: 1px solid rgba(221, 170, 116, 0.86);
  border-radius: 24px;
  box-shadow:
    0 28px 80px rgba(35, 15, 26, 0.42),
    inset 0 0 0 3px rgba(255, 255, 255, 0.72);
  outline: none;
  overscroll-behavior: contain;
}

.profile-crop-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  text-align: left;
}

.profile-crop-crown {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  place-items: center;
  color: #a85a73;
  background: rgba(255, 240, 229, 0.92);
  border: 1px solid rgba(216, 164, 105, 0.72);
  border-radius: 50%;
}

.profile-crop-crown .v-icon {
  color: inherit;
}

.profile-crop-header h2 {
  margin: 1px 0 4px;
  color: #6f3d48;
  font-family: "Cafe24Danjunghae", "Noto Serif KR", serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.25;
}

.profile-crop-header p {
  margin: 0;
  color: #7b6b67;
  font-size: 13px;
  line-height: 1.45;
  word-break: keep-all;
}

.royal-join-page:lang(ja) .profile-crop-header h2 {
  font-family: "GN-Kin-iro_SansSerif", "Yu Mincho", "Hiragino Mincho ProN", serif;
}

.profile-crop-stage {
  position: relative;
  width: min(100%, 320px);
  margin: 0 auto;
  overflow: hidden;
  background: #241820;
  border: 1px solid rgba(228, 180, 122, 0.82);
  border-radius: 20px;
  box-shadow:
    inset 0 0 30px rgba(0, 0, 0, 0.36),
    0 10px 28px rgba(95, 46, 66, 0.16);
}

.profile-cropper {
  width: 100%;
  touch-action: none;
}

.profile-cropper >>> .vuejs-clipper-fixed__wrap {
  touch-action: none;
}

.profile-cropper >>> .vuejs-clipper-fixed__area {
  box-shadow:
    0 0 0 100vw rgba(31, 15, 23, 0.7) !important,
    0 0 0 2px rgba(255, 249, 225, 0.72) !important;
}

.profile-crop-gesture {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: #fff1d2;
  background: rgba(51, 27, 38, 0.68);
  border: 1px solid rgba(255, 224, 170, 0.48);
  border-radius: 50%;
  pointer-events: none;
}

.profile-crop-gesture .v-icon {
  color: inherit;
}

.profile-crop-controls {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 42px;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

.profile-crop-zoom-button {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  color: #9b526b;
  background: #fff;
  border: 1px solid rgba(211, 147, 155, 0.58);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(112, 62, 79, 0.08);
}

.profile-crop-zoom-button .v-icon {
  color: inherit;
}

.profile-crop-zoom-button:disabled,
.profile-crop-reset:disabled,
.profile-crop-actions button:disabled {
  cursor: wait;
  opacity: 0.58;
}

.profile-crop-range {
  width: 100%;
  height: 32px;
  accent-color: #bf6582;
  cursor: pointer;
}

.profile-crop-reset {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin: 5px auto 0;
  padding: 6px 10px;
  color: #8a6e68;
  background: transparent;
  border: 0;
  font-size: 12px;
}

.profile-crop-reset .v-icon {
  color: inherit;
}

.profile-crop-actions {
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.22fr);
  gap: 10px;
  margin-top: 13px;
}

.profile-crop-actions button {
  min-height: 49px;
  padding: 0 15px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
}

.profile-crop-cancel {
  color: #816b67;
  background: #fff;
  border: 1px solid rgba(196, 159, 150, 0.52);
}

.profile-crop-apply {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff4cf;
  background: linear-gradient(118deg, #8c3151, #c75e80 54%, #8c3151);
  border: 1px solid rgba(255, 220, 157, 0.88);
  box-shadow:
    inset 0 1px rgba(255, 255, 255, 0.34),
    0 8px 18px rgba(116, 45, 68, 0.22);
}

.profile-crop-fade-enter-active,
.profile-crop-fade-leave-active {
  transition: opacity 160ms ease;
}

.profile-crop-fade-enter-active .profile-crop-dialog,
.profile-crop-fade-leave-active .profile-crop-dialog {
  transition: transform 180ms ease, opacity 160ms ease;
}

.profile-crop-fade-enter,
.profile-crop-fade-leave-to {
  opacity: 0;
}

.profile-crop-fade-enter .profile-crop-dialog,
.profile-crop-fade-leave-to .profile-crop-dialog {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.petal-layer {
  position: fixed;
  z-index: 4;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.royal-petal {
  position: absolute;
  top: -12vh;
  left: var(--petal-left);
  width: var(--petal-size);
  height: auto;
  opacity: 0.62;
  filter: drop-shadow(0 3px 4px rgba(156, 87, 92, 0.12));
  animation: petal-fall var(--petal-duration) linear var(--petal-delay) infinite;
  will-change: transform;
}

@keyframes petal-fall {
  from {
    transform: translate3d(0, -12vh, 0) rotate(0deg);
  }

  to {
    transform: translate3d(var(--petal-sway), 124vh, 0) rotate(var(--petal-turn));
  }
}

@keyframes royal-submit-surface {
  0%,
  100% {
    background-position: 0% 50%;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.54),
      inset 0 -2px 0 rgba(86, 22, 48, 0.28),
      0 0 0 3px rgba(189, 124, 104, 0.2),
      0 8px 20px rgba(116, 42, 67, 0.25),
      0 0 20px rgba(222, 95, 151, 0.16);
  }

  50% {
    background-position: 100% 50%;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -2px 0 rgba(86, 22, 48, 0.28),
      0 0 0 3px rgba(218, 161, 122, 0.28),
      0 8px 22px rgba(116, 42, 67, 0.28),
      0 0 30px rgba(226, 102, 159, 0.28);
  }
}

@keyframes royal-submit-shimmer {
  0% {
    transform: translate3d(-125%, 0, 0) skewX(-14deg);
  }

  100% {
    transform: translate3d(265%, 0, 0) skewX(-14deg);
  }
}

@keyframes royal-submit-crown-glow {
  0%,
  100% {
    transform: scale(0.96);
  }

  50% {
    transform: scale(1.08);
  }
}

@media (max-width: 600px) {
  .royal-join-page {
    min-height: max(850px, calc(100vw * 2.161));
  }

  .royal-invitation {
    padding: 0 50px 26px;
  }

  .royal-fields {
    margin-top: 5px;
  }

  .royal-field >>> .v-input__control,
  .royal-field >>> .v-input__slot {
    min-height: 40px !important;
  }

  .royal-header {
    margin-bottom: 22px;
  }

  .royal-logo {
    width: 145px;
    height: 45px;
  }

  .mirror-heading h1 {
    font-size: clamp(22px, 6vw, 25px);
  }

  .mirror-section {
    margin-top: 16px;
  }

  .mirror-guidance {
    max-width: 290px;
    font-size: 12px;
    line-height: 1.4;
  }

  .mirror-picker {
    width: min(70vw, 286px);
  }

  .royal-submit {
    min-height: 56px;
    margin-top: 12px;
  }

  .profile-crop-overlay {
    align-items: flex-end;
    padding: 10px;
  }

  .profile-crop-dialog {
    width: 100%;
    max-height: calc(100dvh - 20px);
    padding: 18px 16px 16px;
    border-radius: 22px;
  }

  .profile-crop-header {
    margin-bottom: 12px;
  }

  .profile-crop-header h2 {
    font-size: 20px;
  }

  .profile-crop-header p {
    font-size: 12px;
  }
}

@media (max-width: 360px) {
  .royal-invitation {
    padding-right: 38px;
    padding-left: 38px;
  }

  .royal-logo {
    width: 130px;
  }

  .mirror-heading h1 {
    font-size: clamp(18px, 6vw, 22px);
  }

  .mirror-guidance {
    max-width: 260px;
    font-size: 10.5px;
    line-height: 1.25;
    overflow-wrap: break-word;
  }
}

@media (max-width: 300px) {
  .royal-invitation {
    padding-right: 28px;
    padding-left: 28px;
  }

  .mirror-heading h1 {
    font-size: 18px;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .mirror-picker {
    transition: transform 180ms ease;
  }

  .mirror-picker:hover {
    transform: translateY(-3px);
  }

  .royal-submit {
    animation: royal-submit-surface 4.8s ease-in-out infinite;
    transition: transform 160ms ease, box-shadow 160ms ease;
  }

  .royal-submit:hover {
    transform: translateY(-2px);
  }

  .royal-submit::after {
    animation: royal-submit-shimmer 4.2s cubic-bezier(0.45, 0, 0.2, 1) infinite;
  }

  .royal-submit-crown {
    animation: royal-submit-crown-glow 2.8s ease-in-out infinite;
    transform-origin: center;
    will-change: transform;
  }
}

@media (prefers-reduced-motion: reduce) {
  .royal-petal {
    display: none;
  }

  .royal-submit::before {
    opacity: 1;
  }

  .royal-submit::after {
    display: none;
  }
}
</style>
